import type { BrandConfig, Money } from "@/configurator/domain/models/types";
import type { OfferGroup } from "@/configurator/domain/models/view";
import type {
  OfferRenderInput,
  OfferSpecGroup,
} from "@/configurator/utils/pdf/renderOfferPdf";
import { Loc, t } from "@/locales";
import { formatAmount, formatCurrencyLabel } from "./money";

export type OfferDocumentSource = {
  title: string;
  arShareUrl: string;
  brand: BrandConfig;
  groups: OfferGroup[];
  quantity: number;
  total: Money;
  /** Injected so the caller controls the clock, and so a test can pin it. */
  date: Date;
};

function formatMoney(money: Money): string {
  return `${formatAmount(money)} ${formatCurrencyLabel(money)}`;
}

/** `groupId:optionId` — the key the drawn row and its artwork share. */
const rowKey = (groupId: string, optionId: string) => `${groupId}:${optionId}`;

function toSpecGroups(groups: OfferGroup[]): OfferSpecGroup[] {
  return groups.map((group) => ({
    label: group.label,
    rows: group.rows.map((row) => ({
      key: rowKey(group.id, row.optionId),
      label: row.label,
      color: row.color,
      surcharge: row.surcharge ? `+ ${formatMoney(row.surcharge)}` : undefined,
    })),
  }));
}

function toRowImages(groups: OfferGroup[]): Record<string, string> {
  const images: Record<string, string> = {};
  for (const group of groups) {
    for (const row of group.rows) {
      if (row.image) images[rowKey(group.id, row.optionId)] = row.image;
    }
  }
  return images;
}

/**
 * Turns the configuration into the flat, string-only shape the PDF generator
 * draws. Every catalog and money question is answered here, so `utils/pdf`
 * stays a layout module that knows nothing about the domain.
 */
export function buildOfferRenderInput(
  source: OfferDocumentSource,
  screenshotDataUrl: string | undefined,
): OfferRenderInput {
  return {
    title: source.title,
    copy: {
      specification: t(Loc.offerSpecification),
      quantity: t(Loc.quantityLabel),
      total: t(Loc.offerTotal),
      arHint: t(Loc.arOpen),
      offerDate: t(Loc.offerDate, {
        date: source.date.toLocaleDateString("cs-CZ"),
      }),
    },
    arShareUrl: source.arShareUrl,
    // The badge painted over the 3D view is not always the mark the document
    // should carry, so a config may name a separate one.
    logoSrc: source.brand.pdfLogoSrc ?? source.brand.logoSrc,
    screenshotDataUrl,
    groups: toSpecGroups(source.groups),
    rowImages: toRowImages(source.groups),
    quantity: `${source.quantity} ${t(Loc.quantityUnit)}`,
    total: formatMoney(source.total),
    website: source.brand.website,
  };
}

/** `vyroba-a-design-nabidka-2026-09-16.pdf` */
export function buildOfferFileName(slug: string, date: Date): string {
  return `${slug}-nabidka-${date.toISOString().slice(0, 10)}.pdf`;
}
